/*
  Configurateur de gravure — Atelier de Tao

  Trois rôles :
  1. Nettoyer la saisie en direct (caractères non gravables, emojis, accents optionnels)
  2. Afficher le texte sur la photo de la médaille pendant la frappe
  3. Empêcher l'ajout au panier tant que la gravure n'est pas valide et confirmée

  Le blocage se fait en interceptant l'évènement submit en phase de capture sur
  `document`, et non en désactivant le bouton : Dawn pilote lui-même l'attribut
  `disabled` (rupture de stock, variante indisponible) et les deux logiques
  entreraient en conflit.
*/

const ENGRAVING_BASE_CHARS = "A-Za-z0-9 '’\\-.,&()/+°";
const ENGRAVING_ACCENTED_CHARS = 'À-ÖØ-öø-ÿ';

window.atelierStrings = window.atelierStrings || {
  charactersLeft: '[count]',
  accentsRemoved: '',
  charNotEngravable: '',
  requiredMissing: '',
  confirmRequired: '',
  phone2Missing: '',
  supplementError: '',
};

class EngravingConfigurator extends HTMLElement {
  constructor() {
    super();
    this.onInput = this.onInput.bind(this);
    this.onSubmitCapture = this.onSubmitCapture.bind(this);
    this.refresh = this.refresh.bind(this);
  }

  connectedCallback() {
    this.formId = this.dataset.formId;
    this.allowAccents = this.dataset.allowAccents === 'true';
    this.requireConfirmation = this.dataset.requireConfirmation === 'true';

    this.inputs = Array.from(this.querySelectorAll('[data-engraving-input]'));
    this.finishInputs = Array.from(this.querySelectorAll('[data-engraving-finish]'));
    // L'aperçu peut être rendu dans la colonne photo, donc hors de cet élément :
    // on le cherche d'abord à l'intérieur, puis dans la page par identifiant de section.
    this.preview =
      this.querySelector('[data-engraving-preview]') ||
      document.querySelector(`[data-engraving-preview][data-section-id="${this.dataset.sectionId}"]`);
    this.overlay = this.preview?.querySelector('[data-engraving-overlay]');
    this.previewImage = this.preview?.querySelector('[data-engraving-preview-image]');
    this.movable = this.preview?.querySelector('[data-engraving-movable]');

    // Les commandes de placement sont rendues à côté de l'aperçu, pas dedans.
    const commandes = document.querySelector(
      `[data-engraving-placement][data-section-id="${this.dataset.sectionId}"]`
    );
    this.sizeInput = commandes?.querySelector('[data-engraving-size]');
    this.angleInput = commandes?.querySelector('[data-engraving-angle]');
    this.resetButton = commandes?.querySelector('[data-engraving-reset]');
    this.messages = this.querySelector('[data-engraving-messages]');
    this.recap = this.querySelector('[data-engraving-recap]');
    this.confirmBox = this.querySelector('[data-engraving-confirm]');
    this.summaryInput = this.querySelector('[data-engraving-summary]');
    this.addonToggle = this.querySelector('[data-engraving-addon-toggle]');
    this.addonField = this.querySelector('[data-engraving-addon-field]');
    this.addonInput = this.querySelector('[data-engraving-input="phone2"]');
    this.supplementVariantId = this.dataset.supplementVariantId;
    this.supplementPrice = parseInt(this.dataset.supplementPrice, 10) || 0;
    this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
    this.showPrice = this.dataset.showPrice === 'true';

    this.sanitizePattern = new RegExp(
      `[^${ENGRAVING_BASE_CHARS}${this.allowAccents ? ENGRAVING_ACCENTED_CHARS : ''}]`,
      'g'
    );

    this.measureCanvas = document.createElement('canvas').getContext('2d');

    this.inputs.forEach((input) => {
      input.addEventListener('input', this.onInput);
      input.addEventListener('blur', this.refresh);
    });
    this.finishInputs.forEach((input) => input.addEventListener('change', this.refresh));
    this.confirmBox?.addEventListener('change', () => this.clearMessages());
    this.addonToggle?.addEventListener('change', () => this.toggleAddon());

    document.addEventListener('submit', this.onSubmitCapture, true);
    this.setupPlacement();

    // La photo de la variante sélectionnée devient l'aperçu : la bonne forme,
    // la bonne matière, sans que le client ait à l'imaginer.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartSubscription = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.supplementQueued = false;
      });

      this.variantSubscription = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.sectionId !== this.dataset.sectionId) return;
        this.updatePreviewImage(event.data.variant);
        if (typeof event.data.variant?.price === 'number') {
          this.basePrice = event.data.variant.price;
        }
        this.updatePriceLabel();
      });
    }

    // Le navigateur peut restaurer des valeurs au retour arrière.
    this.refresh();
    this.updatePriceLabel();
    window.addEventListener('resize', this.refresh);

    // La mesure dépend de la mise en page et de la police réellement chargée.
    // Sans ces deux relances, un texte restauré par le navigateur peut être
    // dimensionné avant que la feuille de style ou la police ne soient prêtes.
    window.addEventListener('load', this.refresh);
    document.fonts?.ready.then(this.refresh);
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.onSubmitCapture, true);
    window.removeEventListener('resize', this.refresh);
    window.removeEventListener('load', this.refresh);
    this.variantSubscription?.();
    this.cartSubscription?.();
  }

  /* --------------------------------------------------------------
     Placement du texte sur la photo

     Plusieurs médailles peuvent figurer sur une même image : le client place
     lui-même le texte sur celle qu'il a choisie. Les valeurs de départ viennent
     du thème, on ne les écrase que si le client bouge quelque chose.
     -------------------------------------------------------------- */

  setupPlacement() {
    if (!this.preview) return;

    const style = getComputedStyle(this.preview);
    this.placement = {
      x: parseFloat(style.getPropertyValue('--preview-x')) || 50,
      y: parseFloat(style.getPropertyValue('--preview-y')) || 50,
      taille: parseFloat(style.getPropertyValue('--line-max-ratio')) * 100 || 26,
      angle: parseFloat(style.getPropertyValue('--preview-angle')) || 0,
    };
    this.placementParDefaut = { ...this.placement };
    this.placementModifie = false;

    this.sizeInput?.addEventListener('input', () => {
      this.placement.taille = parseFloat(this.sizeInput.value);
      this.placementModifie = true;
      this.applyPlacement();
    });

    this.angleInput?.addEventListener('input', () => {
      this.placement.angle = parseFloat(this.angleInput.value);
      this.placementModifie = true;
      this.applyPlacement();
    });

    this.resetButton?.addEventListener('click', () => {
      this.placement = { ...this.placementParDefaut };
      if (this.sizeInput) this.sizeInput.value = this.placement.taille;
      if (this.angleInput) this.angleInput.value = this.placement.angle;
      this.placementModifie = false;
      this.applyPlacement();
      this.movable?.focus();
    });

    if (!this.movable) return;

    // Le suivi repose sur un drapeau et des écouteurs posés sur la fenêtre, non
    // sur la capture de pointeur : celle-ci peut échouer, et le geste doit
    // continuer même quand le curseur sort de la zone du texte.
    this.enDeplacement = false;

    const suivre = (event) => {
      if (!this.enDeplacement) return;
      const zone = this.preview.getBoundingClientRect();
      if (!zone.width || !zone.height) return;
      event.preventDefault();
      this.placement.x = this.clamp(((event.clientX - zone.left) / zone.width) * 100);
      this.placement.y = this.clamp(((event.clientY - zone.top) / zone.height) * 100);
      this.placementModifie = true;
      this.applyPlacement();
    };

    const relacher = () => {
      if (!this.enDeplacement) return;
      this.enDeplacement = false;
      this.movable.classList.remove('is-dragging');
      window.removeEventListener('pointermove', suivre);
      window.removeEventListener('pointerup', relacher);
      window.removeEventListener('pointercancel', relacher);
    };

    this.movable.addEventListener('pointerdown', (event) => {
      // Un seul doigt : sur mobile, deux doigts doivent rester au défilement
      if (event.isPrimary === false) return;
      event.preventDefault();
      this.enDeplacement = true;
      this.movable.classList.add('is-dragging');
      try {
        this.movable.setPointerCapture(event.pointerId);
      } catch (e) {
        // Sans capture, les écouteurs de fenêtre suffisent
      }
      window.addEventListener('pointermove', suivre);
      window.addEventListener('pointerup', relacher);
      window.addEventListener('pointercancel', relacher);
      suivre(event);
    });

    // Sans clavier, le placement serait inaccessible à qui n'utilise pas de souris
    this.movable.addEventListener('keydown', (event) => {
      const pas = event.shiftKey ? 5 : 1;
      const deplacements = {
        ArrowLeft: [-pas, 0], ArrowRight: [pas, 0],
        ArrowUp: [0, -pas], ArrowDown: [0, pas],
      };
      const d = deplacements[event.key];
      if (!d) return;
      event.preventDefault();
      this.placement.x = this.clamp(this.placement.x + d[0]);
      this.placement.y = this.clamp(this.placement.y + d[1]);
      this.placementModifie = true;
      this.applyPlacement();
    });
  }

  clamp(valeur) {
    return Math.min(100, Math.max(0, Math.round(valeur * 10) / 10));
  }

  applyPlacement() {
    if (!this.preview) return;
    this.preview.style.setProperty('--preview-x', `${this.placement.x}%`);
    this.preview.style.setProperty('--preview-y', `${this.placement.y}%`);
    this.preview.style.setProperty('--line-max-ratio', this.placement.taille / 100);
    this.preview.style.setProperty('--preview-angle', `${this.placement.angle}deg`);
    this.movable?.setAttribute('aria-valuetext',
      `${Math.round(this.placement.x)} % / ${Math.round(this.placement.y)} %`);
    this.updateOverlay();
    this.updateSummary();
  }

  /* --------------------------------------------------------------
     Second numéro, facturé en supplément
     -------------------------------------------------------------- */

  toggleAddon() {
    const active = !!this.addonToggle?.checked;
    if (this.addonField) this.addonField.hidden = !active;
    if (this.addonInput) {
      this.addonInput.disabled = !active;
      if (!active) this.addonInput.value = '';
      else this.addonInput.focus();
    }
    this.clearMessages();
    this.refresh();
    this.updatePriceLabel();
  }

  needsSupplement() {
    return !!(this.addonToggle?.checked && this.supplementVariantId);
  }

  /* --------------------------------------------------------------
     Prix affiché dans le bouton d'ajout
     -------------------------------------------------------------- */

  formatMoney(cents) {
    const devise = window.Shopify?.currency?.active || 'EUR';
    const langue = window.Shopify?.locale || document.documentElement.lang || 'fr';
    try {
      return new Intl.NumberFormat(langue, { style: 'currency', currency: devise }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  // Dawn réécrit le texte du bouton à chaque changement de variante, puis publie
  // variantChange : on repasse derrière lui, jamais avant.
  updatePriceLabel() {
    if (!this.showPrice) return;

    const bouton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
    const texte = bouton?.querySelector('span');
    if (!texte) return;

    // Bouton désactivé : Dawn y affiche « rupture de stock », on n'y touche pas.
    if (bouton.hasAttribute('disabled') || !this.basePrice) return;

    const total = this.basePrice + (this.needsSupplement() ? this.supplementPrice : 0);
    const libelle = window.variantStrings?.addToCart || texte.textContent.split(' — ')[0];
    texte.textContent = `${libelle} — ${this.formatMoney(total)}`;
  }

  // Le supplément est une ligne de panier distincte : une propriété de ligne ne
  // peut pas modifier un prix sur Shopify.
  async addSupplement() {
    const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
    const nameInput = this.inputs.find((i) => i.dataset.engravingInput === 'name');

    const response = await fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: Number(this.supplementVariantId),
            quantity: 1,
            properties: { _pour: nameInput?.value.trim() || '—' },
          },
        ],
      }),
    });

    if (!response.ok) throw new Error(`cart/add ${response.status}`);
    return response.json();
  }

  /* --------------------------------------------------------------
     Saisie
     -------------------------------------------------------------- */

  onInput(event) {
    const input = event.target;
    const original = input.value;
    const cleaned = this.sanitize(original);

    if (cleaned !== original) {
      // Conserver la position du curseur malgré la réécriture de la valeur
      const position = input.selectionStart - (original.length - cleaned.length);
      input.value = cleaned;
      if (input.setSelectionRange) {
        input.setSelectionRange(Math.max(0, position), Math.max(0, position));
      }
      this.flagStrippedCharacters(original, cleaned);
    }

    this.refresh();
  }

  sanitize(value) {
    let output = value;

    if (!this.allowAccents) {
      // Décompose puis retire les diacritiques : « Rémi » devient « Remi »
      output = output.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    // Les lettres sont des capitales physiques : on aligne la saisie sur le produit
    return output.toUpperCase().replace(this.sanitizePattern, '').replace(/\s{2,}/g, ' ');
  }

  flagStrippedCharacters(original, cleaned) {
    const hadAccents = /[À-ÿ]/.test(original);

    if (!this.allowAccents && hadAccents) {
      this.showMessage(window.atelierStrings.accentsRemoved, 'info');
    } else if (cleaned.trim().length < original.trim().length) {
      this.showMessage(window.atelierStrings.charNotEngravable, 'info');
    }
  }

  /* --------------------------------------------------------------
     Aperçu et compteurs
     -------------------------------------------------------------- */

  refresh() {
    this.inputs.forEach((input) => this.updateCounter(input));
    this.updateOverlay();
    this.updateRecap();
    this.updateSummary();
  }

  updateCounter(input) {
    const key = input.dataset.engravingInput;
    const counter = this.querySelector(`[data-engraving-counter="${key}"]`);
    if (!counter) return;

    const max = parseInt(input.getAttribute('maxlength'), 10);
    const used = input.value.length;
    counter.textContent = window.atelierStrings.charactersLeft.replace('[count]', max - used);
    counter.classList.toggle('atelier-engraving__counter--full', used >= max);
  }

  updateOverlay() {
    if (!this.overlay) return;

    const finish = this.selectedFinishKey();
    this.overlay.classList.toggle('atelier-engraving__overlay--gold', finish !== 'silver');
    this.overlay.classList.toggle('atelier-engraving__overlay--silver', finish === 'silver');

    this.overlay.querySelectorAll('[data-engraving-preview-line]').forEach((line) => {
      const key = line.dataset.engravingPreviewLine;
      const input = this.inputs.find((i) => i.dataset.engravingInput === key);
      line.textContent = input ? input.value : '';
      this.fitLine(line);
    });
  }

  // Ajuste la taille du texte pour qu'il tienne dans la largeur gravable,
  // exactement comme la machine réduit la gravure sur un nom long.
  fitLine(line) {
    if (!line.textContent) {
      line.style.fontSize = '';
      return;
    }

    const boxWidth = this.overlay.clientWidth;
    if (!boxWidth) return;

    const styles = getComputedStyle(line);
    const ratio = parseFloat(styles.getPropertyValue('--line-max-ratio')) || 0.2;
    const maxSize = boxWidth * ratio;

    this.measureCanvas.font = `${styles.fontStyle} ${styles.fontWeight} 100px ${styles.fontFamily}`;

    // measureText ignore letter-spacing : on l'ajoute à la main, sinon les noms
    // longs débordent de la zone.
    const tracking = parseFloat(getComputedStyle(this.overlay).getPropertyValue('--preview-tracking')) || 0;
    const text = line.textContent;
    const widthAt100 = this.measureCanvas.measureText(text).width + text.length * tracking * 100;
    if (!widthAt100) return;

    const size = Math.min(maxSize, (boxWidth / widthAt100) * 100);
    if (size > 0) line.style.fontSize = `${size}px`;
  }

  selectedFinishKey() {
    const checked = this.finishInputs.find((input) => input.checked);
    return checked?.dataset.engravingFinish || 'gold';
  }

  selectedFinishName() {
    const checked = this.finishInputs.find((input) => input.checked);
    return checked?.value || '';
  }

  updatePreviewImage(variant) {
    if (!this.previewImage || !variant?.featured_image?.src) return;

    const src = variant.featured_image.src.replace(/(\.[a-z]+)(\?.*)?$/i, '_900x$1');
    this.previewImage.src = src;
    this.previewImage.removeAttribute('srcset');
  }

  /* --------------------------------------------------------------
     Récapitulatif et validation
     -------------------------------------------------------------- */

  filledValues() {
    return this.inputs
      .filter((input) => !input.disabled)
      .map((input) => ({ key: input.dataset.engravingInput, value: input.value.trim() }))
      .filter((entry) => entry.value !== '');
  }

  updateRecap() {
    if (!this.recap) return;
    const parts = this.filledValues().map((entry) => entry.value);
    this.recap.textContent = parts.length ? `« ${parts.join(' / ')} »` : '—';
  }

  // Chaîne stable pour l'atelier, indépendante de la langue du client.
  updateSummary() {
    if (!this.summaryInput) return;

    const parts = this.inputs
      .map((input) => {
        const value = input.value.trim();
        if (!value || input.disabled) return null;
        return `${input.dataset.summaryKey || input.dataset.engravingInput.toUpperCase()}=${value}`;
      })
      .filter(Boolean);

    const finish = this.selectedFinishName();
    if (finish) parts.push(`FINITION=${finish}`);

    // Position et taille voulues par le client, en repères de l'image
    if (this.placementModifie && this.placement) {
      const { x, y, taille, angle } = this.placement;
      let repere = `PLACEMENT=${Math.round(x)}/${Math.round(y)} TAILLE=${Math.round(taille)}`;
      if (Math.round(angle) !== 0) repere += ` ANGLE=${Math.round(angle)}`;
      parts.push(repere);
    }

    this.summaryInput.value = parts.length ? parts.join(' | ') : '';
  }

  validate() {
    const missing = this.inputs.filter(
      (input) => input.hasAttribute('data-engraving-required') && input.value.trim() === ''
    );

    if (missing.length) {
      return { valid: false, message: window.atelierStrings.requiredMissing, focus: missing[0] };
    }

    if (this.addonToggle?.checked && this.addonInput && this.addonInput.value.trim() === '') {
      return { valid: false, message: window.atelierStrings.phone2Missing, focus: this.addonInput };
    }

    if (this.requireConfirmation && this.confirmBox && !this.confirmBox.checked) {
      return { valid: false, message: window.atelierStrings.confirmRequired, focus: this.confirmBox };
    }

    return { valid: true };
  }

  onSubmitCapture(event) {
    if (event.target?.id !== this.formId) return;

    const result = this.validate();
    if (result.valid) {
      this.clearMessages();
      if (this.needsSupplement() && !this.supplementQueued) this.handleSupplement(event);
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.showMessage(result.message, 'error');
    result.focus?.focus();
    result.focus?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  handleSupplement(event) {
    event.preventDefault();
    event.stopPropagation();
    this.supplementQueued = true;

    const form = event.target;
    this.addSupplement()
      .then(() => {
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      })
      .catch(() => {
        // Sans le supplément, l'article partirait au mauvais prix : on n'ajoute rien.
        this.supplementQueued = false;
        this.showMessage(window.atelierStrings.supplementError, 'error');
      });
  }

  /* --------------------------------------------------------------
     Messages
     -------------------------------------------------------------- */

  showMessage(text, level) {
    if (!this.messages || !text) return;
    this.messages.textContent = text;
    this.messages.className = `atelier-engraving__messages atelier-engraving__messages--${level}`;

    if (level === 'info') {
      clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => this.clearMessages(), 6000);
    }
  }

  clearMessages() {
    if (!this.messages) return;
    this.messages.textContent = '';
    this.messages.className = 'atelier-engraving__messages';
  }
}

customElements.define('engraving-configurator', EngravingConfigurator);
